import { isImageContentType } from './attachments';

// Match the synchronous Rails pipeline bound before degrading to ping + poll.
const REQUEST_TIMEOUT_MS = 60_000;

export function isRelayableMediaContentType(contentType) {
  return isImageContentType(contentType) || contentType === 'application/pdf';
}

function failure(reason, response) {
  return { status: 'fallback', reason, responseStatus: response?.status ?? null };
}

// Calls the Lambda from the browser, then persists its signed variants response through
// the existing owner-scoped API. All failures intentionally return a fallback result: the
// B2 webhook and existing ping/poll path remain the reliable backstop.
export async function relayMediaAttachmentProcessing({
  attachmentId,
  lambdaPayload,
  processMediaAttachmentUrl,
  isProcessingComplete,
  patchRequest,
  fetchRequest = fetch,
}) {
  if (!attachmentId || !lambdaPayload || !processMediaAttachmentUrl) return { status: 'skipped' };

  let requestFailureReason = 'relay-request-failed';

  try {
    const signal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
    const relayResponse = await fetchRequest(processMediaAttachmentUrl, {
      method: 'POST',
      body: JSON.stringify({ lambda_payload: lambdaPayload }),
      signal,
    });
    if (!relayResponse.ok) return failure('relay-rejected', relayResponse);

    const relayPayload = await relayResponse.json().catch(() => null);
    const jwt = relayPayload?.ok ? relayPayload.data?.jwt : null;
    if (!jwt) return failure('relay-invalid-response', relayResponse);

    requestFailureReason = 'relay-patch-failed';
    const patchResponse = await patchRequest(
      `/media_attachments/${attachmentId}`,
      { jwt },
      { signal },
    );
    if (!patchResponse.ok) return failure('relay-patch-rejected', patchResponse);

    const patchPayload = await patchResponse.json().catch(() => null);
    const attachment = patchPayload?.ok ? patchPayload.data?.media_attachment : null;
    if (!attachment?.id) return failure('relay-patch-invalid-response', patchResponse);
    if (!isProcessingComplete(attachment)) return failure('relay-patch-incomplete', patchResponse);

    return { status: 'completed', attachment };
  } catch {
    return failure(requestFailureReason);
  }
}
